import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchSensorData } from '../api/sensorApi';

/**
 * Custom hook for fetching and managing sensor data
 * @param {string} tunnel - Optional tunnel ID to filter data by
 * @returns {Object} Object containing loading state, data, error, and functions to filter data
 */
const useSensorData = (initialTunnel = null) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [selectedTunnel, setSelectedTunnel] = useState(initialTunnel);

  // Track render count
  const renderCount = useRef(0);

  // Load data with current tunnel filter (memoized to prevent re-creating on every render)
  const loadData = useCallback(async (tunnel = selectedTunnel) => {
    try {
      setLoading(true);
      const sensorData = await fetchSensorData(tunnel);
      
      // Limit console logs
      renderCount.current += 1;
      if (renderCount.current % 5 === 0) {
        console.log(`useSensorData render #${renderCount.current}, tunnel:`, tunnel);
      }
      
      setData(sensorData);
      setError(null);
    } catch (err) {
      console.error('Error fetching sensor data:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Change tunnel (memoized to prevent re-creating on every render)
  const changeTunnel = useCallback((tunnel) => {
    if (tunnel !== selectedTunnel) {
      console.log('Changing tunnel from', selectedTunnel, 'to', tunnel);
      setSelectedTunnel(tunnel);
    }
  }, [selectedTunnel]);

  // Ref for tracking initial load
  const initialLoadDone = useRef(false);

  // Effect for initial data load and tunnel changes
  // Combine into a single effect to prevent duplicate loads
  useEffect(() => {
    if (!initialLoadDone.current) {
      // Initial load
      loadData();
      initialLoadDone.current = true;
    } else {
      // Subsequent tunnel changes
      loadData(selectedTunnel);
    }
  }, [selectedTunnel, loadData]);
  
  // Method to force a data reload (useful when reading type changes)
  const reloadData = useCallback(() => {
    console.log('Forcing data reload');
    loadData(selectedTunnel);
  }, [selectedTunnel, loadData]);

  return { 
    loading, 
    data, 
    error, 
    selectedTunnel,
    changeTunnel,
    loadData,
    reloadData
  };
};

export default useSensorData;